import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const Chatbot = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your SignCrypt assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputText.trim() && !uploadedFile) return;

    const userMessageText = inputText.trim();
    setInputText('');
    setIsUploading(true);

    // Prepare display text (with file info if present)
    let displayText = userMessageText;
    if (uploadedFile) {
      displayText = userMessageText 
        ? `${userMessageText}\n\n[Uploaded: ${uploadedFile.name}]` 
        : `[Uploaded: ${uploadedFile.name}]`;
    }

    const userMessage = {
      id: Date.now(),
      text: displayText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      let response;
      
      if (uploadedFile) {
        // Handle file upload
        const formData = new FormData();
        formData.append('message', userMessageText || 'Please analyze this file');
        formData.append('user_id', 'signcrypt_user');
        formData.append('file', uploadedFile);
        formData.append('context', JSON.stringify({
          app: 'signcrypt',
          features: ['sign_language', 'morse_code', 'encryption', 'sos']
        }));

        response = await fetch('https://bharathchilaka-test.hf.space/api/chatbot', {
          method: 'POST',
          body: formData
        });
      } else {
        // Handle regular text message
        response = await fetch('https://bharathchilaka-test.hf.space/api/chatbot', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: userMessageText,
            user_id: 'signcrypt_user',
            context: {
              app: 'signcrypt',
              features: ['sign_language', 'morse_code', 'encryption', 'sos']
            }
          })
        });
      }

      const data = await response.json();
      
      const botResponse = data.response?.response || data.response || 'I received your message but no response was provided.';
      
      const botMessage = {
        id: Date.now() + 1,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chatbot API error:', error);
      // Use local fallback responses when external API is unavailable
      const fallbackResponse = generateBotResponse(userMessageText || 'file');
      const errorMessage = {
        id: Date.now() + 1,
        text: fallbackResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsUploading(false);
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Railway chatbot integration - fallback responses kept for offline mode
  const generateBotResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    if (input.includes('sign language') || input.includes('gesture')) {
      return "Sign language recognition uses your device's camera to detect hand gestures and convert them to text. Make sure you have good lighting and your hands are clearly visible!";
    } else if (input.includes('morse') || input.includes('code')) {
      return "Morse code detection works by recognizing hand signals - OK gesture for dots and Swag gesture for dashes. Try making these gestures in front of your camera!";
    } else if (input.includes('encrypt') || input.includes('decrypt')) {
      return "Encryption keeps your messages secure. Use the Encrypt page to secure your messages and the Decrypt page to read encrypted messages.";
    } else if (input.includes('sos') || input.includes('emergency')) {
      return "The SOS feature sends emergency alerts to your registered contacts. Make sure to add emergency contacts in the SOS section first!";
    } else if (input.includes('help') || input.includes('how')) {
      return "I can help you with sign language recognition, morse code detection, encryption, SOS alerts, and general app usage. What would you like to know more about?";
    } else {
      return "I'm here to help! You can ask me about sign language recognition, morse code detection, encryption features, SOS alerts, or any other app functionality.";
    }
  };

  const handleSpeechInput = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognition.start();
    } else {
      alert('Speech recognition is not supported in this browser.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="chatbot-modal">
      <div className="chatbot-content">
        <div className="chatbot-header">
          <h3>SignCrypt Assistant</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="chatbot-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              <div className="message-content">
                {message.text}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="message bot">
              <div className="message-content typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input">
          <div className="input-container">
            {uploadedFile && (
              <div className="uploaded-file-preview">
                <span className="file-name">📎 {uploadedFile.name}</span>
                <button 
                  className="remove-file-btn"
                  onClick={removeUploadedFile}
                  title="Remove file"
                >
                  ✕
                </button>
              </div>
            )}
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              placeholder={uploadedFile ? "Add a message (optional)..." : "Type your message..."}
              rows="1"
              className="message-input"
            />
            
            <div className="input-actions">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="file-input-hidden"
                accept=".txt,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.csv"
              />
              <button
                className="file-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Upload file"
                disabled={isUploading}
              >
                📁
              </button>
              <button
                className={`mic-btn ${isListening ? 'listening' : ''}`}
                onClick={handleSpeechInput}
                title="Voice Input"
                disabled={isListening || isUploading}
              >
                {isListening ? '🛑' : '🎤'}
              </button>
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={(!inputText.trim() && !uploadedFile) || isUploading}
              >
                {isUploading ? '⏳' : '➤'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;